import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import Odontogram from './Odontogram';
import Pagination from './Pagination';
import { Smile, Search, User, Phone, CreditCard } from 'lucide-react';

const OdontogramPage = ({ currentBranch, user }) => {
  const { print_lang } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchPatients = useCallback(async (search = '', resetPage = false) => {
    setIsLoading(true);
    try {
      const page = resetPage ? 1 : currentPage;
      if (resetPage) setCurrentPage(1);

      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id,
        status: 'active',
        page: page,
        limit: pageSize
      });
      if (search || searchTerm) {
        params.append('search', search || searchTerm);
      }
      const data = await fetchWithAuth(`/api/patients?${params}`);
      setPatients(data.data || []);

      if (data.pagination) {
        setTotalCount(data.pagination.totalCount || 0);
        setTotalPages(data.pagination.totalPages || 0);
      }
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentBranch, currentPage, pageSize, searchTerm]);

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchPatients();
    }
  }, [currentBranch, currentPage, pageSize, fetchPatients]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPatients(searchTerm, true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
  };

  // If patient is selected, show Odontogram
  if (selectedPatient) {
    return (
      <div>
        {/* Patient Info Header */}
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="btn btn-secondary"
              >
                ← {print_lang('back', 'Back')}
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={24} className="text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">
                    {selectedPatient.prefix} {selectedPatient.name}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    {selectedPatient.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={14} /> {selectedPatient.phone}
                      </span>
                    )}
                    {selectedPatient.citizen_id && (
                      <span className="flex items-center gap-1">
                        <CreditCard size={14} /> {selectedPatient.citizen_id}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Drug Allergy Warning */}
            {selectedPatient.drug_allergy && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                <p className="text-sm font-medium text-red-700">
                  {print_lang('drug_allergy', 'Drug Allergy')}: {selectedPatient.drug_allergy}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Odontogram */}
        <Odontogram
          patientId={selectedPatient.patient_id}
          currentBranch={currentBranch}
        />
      </div>
    );
  }

  // Patient Selection View
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Smile size={28} />
          {print_lang('odontogram', 'Odontogram')}
        </h1>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="card mb-6">
        <h3 className="text-lg font-medium mb-4">
          {print_lang('select_patient', 'Select Patient')}
        </h3>
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={print_lang('search_patient_placeholder', 'Search by name, phone, or citizen ID...')}
                className="pl-10"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">
            {print_lang('search', 'Search')}
          </button>
        </div>
      </form>

      {/* Patient List */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Smile size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{print_lang('no_patients_found', 'No patients found. Search to find a patient.')}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-3">
              {patients.map((patient) => (
                <button
                  key={patient.patient_id}
                  onClick={() => handleSelectPatient(patient)}
                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all text-left w-full"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <User size={24} className="text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800">
                      {patient.prefix} {patient.name}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {patient.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {patient.phone}
                        </span>
                      )}
                      {patient.citizen_id && (
                        <span className="flex items-center gap-1">
                          <CreditCard size={12} /> {patient.citizen_id}
                        </span>
                      )}
                    </div>
                  </div>
                  {patient.drug_allergy && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                      {print_lang('allergy', 'Allergy')}
                    </span>
                  )}
                  <div className="text-blue-500">
                    <Smile size={20} />
                  </div>
                </button>
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              print_lang={print_lang}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OdontogramPage;
